<i18n src="./translations"></i18n>

<template>
  <PdpTabBase
    class="pdp-description-tab"
    :headline="$t('pdp-description-tab.header')"
    :class="classes"
    :data-preload="[$options.name]"
    :is-expanded="activeTab"
    :as-plain="plainTabBase"
  >
    <PdpVehicleNotification
      v-if="carAttributes.length && !isDropShipment"
      class="pdp-vehicle-notification-description"
      :gtm-class-name="'gtm--pdp-description-vehicle-notification'"
      :vehicle-compatibility="vehicleCompatibility"
      :vehicle-name="vehicleName"
      :is-car-selected="isCarSelected"
      :car-attributes="carAttributes"
      attributes-variant="list-expanded"
    />

    <div
      v-if="itemFeaturesWithLinks && !!itemFeaturesWithLinks.length"
      class="table features-table"
    >
      <h4>{{ $t('pdp-description-tab.features_header') }}</h4>
      <PdpTable
        :lines="itemFeaturesWithLinks"
        :columns="[
          { key: 'title', label: '' },
          { key: 'value', label: '' },
        ]"
        :show-headers="false"
        modifier="alternating"
      >
        <template #value="{ line }">
          <a
            v-if="line.attributeLink"
            class="attribute-link"
            :href="line.attributeLink"
            data-testid="pdp-description-features-attribute-link"
          >
            {{ line.value }}
          </a>
          <span v-else>{{ line.value }}</span>
        </template>
      </PdpTable>
    </div>

    <TyreLabels :attributes="features" :images="images"></TyreLabels>

    <ProductBundles
      v-if="productBundles && productBundles.length"
      :items="productBundles"
    />

    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="description"
      class="pdp-description-tab__description rich-html"
      v-html="description"
    />
    <!-- eslint-disable vue/no-v-html -->

    <template v-if="filteredDocuments && filteredDocuments.length">
      <h5 class="header">{{ $t('pdp-description-tab.document_header') }}</h5>
      <DocumentsList :documents="filteredDocuments" />
    </template>
    <template v-if="hazardousGoodInfo && isHazardousGood">
      <h5 class="header">
        {{ $t('pdp-description-tab.hazardous-good-header') }}
      </h5>
      <HazardousGood21
        :information="hazardousGoodInformation"
        :documents="{ items: documents }"
      />
    </template>
    <!-- For testing, to be removed -->
    <div style="display: none; background-color: green; padding: 10px;">
      <HazardousGood
        class="pdp-description-tab__hazardous-good"
        :information="hazardousGoodInformation"
        :documents="{ items: documents }"
        :is-collapsible="false"
        modifier="secondary"
      />
    </div>
  </PdpTabBase>
</template>

<script>
import PdpTable from 'Components/01-atoms/pdp-table/PdpTable';
import globalMixin from 'Libs/mixins/globalMixin';
import DocumentsList from 'Components/01-atoms/documents-list/DocumentsList.vue';
import HazardousGood21 from 'Components/02-molecules/hazardous-good-21/HazardousGood21.vue';
import HazardousGood from 'Components/02-molecules/hazardous-good/HazardousGood.vue';
import PdpTabBase from 'Components/01-atoms/pdp-tab-base/PdpTabBase';
import ProductBundles from 'Components/02-molecules/product-bundles/ProductBundles';
import TyreLabels from 'Components/02-molecules/tyre-labels/TyreLabels';
import PdpVehicleNotification from 'Components/02-molecules/pdp-vehicle-notification/PdpVehicleNotification';
import { isTypeLabel } from 'Components/02-molecules/tyre-labels/models/tyre-labels';
import { getLinesWithAttributeLinks } from 'Libs/helpers/attribute-catalog-search';

export default {
  name: 'PdpDescriptionTab',
  components: {
    HazardousGood21,
    HazardousGood,
    PdpTable,
    DocumentsList,
    PdpTabBase,
    ProductBundles,
    TyreLabels,
    PdpVehicleNotification,
  },
  mixins: [globalMixin],
  props: {
    restrictions: {
      type: Array,
      default: () => [],
    },
    carAttributes: {
      type: Array,
      default: () => [],
    },
    isCarSelected: {
      type: Boolean,
      default: false,
    },
    vehicleCompatibility: {
      type: String,
      default: 'UNIVERSAL_PRODUCT',
    },
    vehicleName: {
      type: String,
      default: '',
    },
    isDropShipment: {
      type: Boolean,
      default: false,
    },
    productBundles: {
      type: Array,
      default: () => [],
    },
    features: {
      type: Array,
      default: () => [],
    },
    description: {
      type: String,
      default: '',
    },
    documents: {
      type: Array,
      default: () => [],
    },
    isHazardousGood: {
      type: Boolean,
      default: false,
    },
    hazardousGoodInformation: {
      type: Object,
      default: () => ({}),
    },
    hazardousGoodInfo: {
      type: Boolean,
      default: false,
    },
    activeTab: {
      type: Boolean,
      default: false,
    },
    images: {
      type: Array,
      default: () => [],
    },
    plainTabBase: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    itemFeaturesWithLinks() {
      const itemFeatures = this.features.filter(
        (feature) => !isTypeLabel(feature?.id)
      );

      return getLinesWithAttributeLinks({
        lines: itemFeatures,
      });
    },
    filteredDocuments() {
      let documents = [];
      // Here we need to distinguish: this.hazardousGoodInfo > A SC key
      // this.isHazardousGood > If the product itself is a hazardous good
      if (
        this.documents.length &&
        this.hazardousGoodInfo &&
        this.isHazardousGood
      ) {
        documents = this.documents.filter(
          (doc) => doc.title !== 'Sicherheitsdatenblatt'
        );
      } else {
        documents = this.documents;
      }
      return documents;
    },
  },
};
</script>

<style scoped lang="scss">
@import 'mixins';
@import 'variables/breakpoints';

@mixin default-typography {
  font-size: var(--font-size-M);
  line-height: var(--font-size-LLXL);
}

.pdp-vehicle-notification-description {
  margin-bottom: 40px;
}

.features-table {
  max-height: none;
}

.table {
  h4 {
    font-size: var(--font-size-M);
    font-weight: bold;
    margin-bottom: var(--space-0--half);
  }

  .pdp-table {
    margin-top: var(--space-1--half);
  }

  .attribute-link {
    color: var(--color-nero);
    text-decoration: underline;
  }
}

.pdp-description-tab__description {
  margin-top: 40px;

  @include default-typography;
}

.bundles {
  margin-top: var(--space-5);

  @media #{$_mediaMDown} {
    margin-top: var(--space-4);
  }
}

@media #{$_mediaMDown} {
  ::v-deep .pdp-table__table-cell:last-child {
    text-align: right;
  }
}

.header {
  font-size: var(--font-size-MML);
  font-weight: bold;
  margin-top: var(--space-3);
  margin-bottom: var(--space-2);
}
</style>
